// src/api/composition.ts
//
// Composition builder API (plan 2026-09-02, fase C): the field catalog a
// builder may pick from and the one-off preview of a query. Both are
// server-scoped to the caller's grants; the client sends no source scope.
import { apiJson } from "./client";
import { ANY_DASHBOARD, notifyDashboardMutated } from "./dashboardMutations";
import type { components } from "@verity/shared-types";
import { ARRANGE_CHOICES as GENERATED_ARRANGE_CHOICES } from "@verity/shared-types/composition-arrange";

export type CompositionCatalog = components["schemas"]["CompositionCatalogDTO"];
/** A dimension of another dataset B that a query on this dataset can group
 *  or filter by over a join path, stored as `{key, via}` (plan 2026-10-10-
 *  semantic-entities-and-conversion, E4). Written out here until the shared
 *  types are regenerated with `CatalogJoinedDimensionDTO`. */
export type CatalogJoinedDimension = {
  via: string;
  dataset_label: string;
  key: string;
  label: string;
  description?: string | null;
  role?: string;
};
export type CatalogDataset = components["schemas"]["CatalogDatasetDTO"] & {
  joined_dimensions?: CatalogJoinedDimension[];
};
export type CatalogSource = Omit<components["schemas"]["CatalogSourceDTO"], "datasets"> & {
  datasets?: CatalogDataset[];
};
export type CatalogDimension = components["schemas"]["CatalogDimensionDTO"];
export type CatalogMetric = components["schemas"]["CatalogMetricDTO"];
export type CompositionPreviewRequest = components["schemas"]["CompositionPreviewRequest"];
export type CompositionPreviewResponse = components["schemas"]["CompositionPreviewResponse"];

/** Block types the builder offers. Data blocks carry a query whose intent
 *  must fit: kpi = single-value, chart = time-series | categorical,
 *  table = categorical (rows by dimension) | time-series (rows per period)
 *  (mirrors services/composition/blocks.py). */
// `analysis` is report-only: the register tags every type with the surfaces
// it may be placed on (`services/composition/blocks.py`, ADR-0242 rule 3), and
// the emitted layout spec carries that tag — `offeredBlockTypes(surface)` in
// `dashboards/composition/blocks.ts` reads it, a drift guard pins this union
// against the spec.
// `control` is placed only by Verity's managed templates (plan
// 2026-09-21-a-reusable-control-block-and-the-attribution-page): registered and
// sized like any block, never offered (`offered: false` in the spec).
export type BlockType =
  | "text" | "heading" | "kpi" | "chart" | "table" | "analysis" | "agent_deep_dive" | "shape" | "control"
  // Prose with live variables (plan 2026-10-16-a-smart-text-block): a data
  // block whose query is derived from its variables; never a placeholder.
  | "smart_text"
  // A picture from an image library (plan 2026-10-23-an-image-block-on-a-
  // dashboard): no query, dashboard only; `offered: false` until a browser
  // has drawn it.
  | "image"
  // The fixed layer of the weekly e-commerce report (plan 2026-10-13, C3):
  // registered and sized, placed by Verity's templates only (`offered: false`).
  | "anchors" | "bridge" | "cohort_returns" | "attribution_table" | "follow_up";
export const DATA_BLOCK_TYPES: readonly BlockType[] = ["kpi", "chart", "table"];
export const BLOCK_INTENTS: Record<"kpi" | "chart" | "table", readonly string[]> = {
  kpi: ["single-value"],
  chart: ["time-series", "categorical"],
  table: ["categorical", "time-series"],
};

/** The caller's catalog. With `dashboardId` (a dashboard the caller may edit)
 *  the server also merges in the data sources that dashboard shares (plan
 *  2026-10-05 dashboard data sources, ADR-0346); without it, only the
 *  caller's own sources. */
export async function getCompositionCatalog(dashboardId?: string | null): Promise<CompositionCatalog> {
  const query = dashboardId ? `?dashboard_id=${encodeURIComponent(dashboardId)}` : "";
  return apiJson<CompositionCatalog>(`/composition/catalog${query}`);
}

export async function previewComposition(
  body: CompositionPreviewRequest,
): Promise<CompositionPreviewResponse> {
  return apiJson<CompositionPreviewResponse>("/composition/preview", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type FilterImpactRequest = components["schemas"]["FilterImpactRequest"];
export type FilterImpactResponse = components["schemas"]["FilterImpactResponse"];

/** What a filter being edited does to the numbers (plan 2026-10-11 filter
 *  modal with impact, Decision 4). Read-only; the filter modal debounces it. */
export async function getFilterImpact(body: FilterImpactRequest): Promise<FilterImpactResponse> {
  return apiJson<FilterImpactResponse>("/composition/filter-impact", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type FillBlockRequest = components["schemas"]["FillBlockRequest"];
export type FillBlockResponse = components["schemas"]["FillBlockResponse"];

/**
 * One turn of the block builder (plan 2026-09-02 block builder agent).
 *
 * The server owns the conversation: the first turn omits `conversation_id`
 * and the response carries the one to send back on every later turn. A turn
 * that produced a query answers with `ir` + `preview`; a turn that could not
 * answers with text only.
 */
export async function fillBlock(body: FillBlockRequest): Promise<FillBlockResponse> {
  const result = await apiJson<FillBlockResponse>("/composition/fill", {
    method: "POST",
    body: JSON.stringify(body),
  });
  // A fill writes the block (the dashboard's revision moves), but the path
  // names no dashboard: the page on screen is the one to refresh (ADR-0228).
  notifyDashboardMutated(ANY_DASHBOARD);
  return result;
}

export type FillReportBlockRequest = components["schemas"]["FillReportBlockRequest"];
export type FillReportBlockResponse = components["schemas"]["FillReportBlockResponse"];

/**
 * One turn of the block builder on a REPORT block (plan 2026-09-02
 * fill-with-chat-report-blocks, R5/R6).
 *
 * The report twin of `fillBlock`: same model, prompt, schema and retry
 * (`fill_block_turn`, byte for byte) — only the container and the preview
 * differ. The answer's `report_preview` is what the report will actually
 * render (ADR-0202: the report's hook resolver, not the dashboard's
 * kernel); `preview`/`ir` stay the builder's own bookkeeping.
 */
export async function fillReportBlock(
  body: FillReportBlockRequest,
): Promise<FillReportBlockResponse> {
  return apiJson<FillReportBlockResponse>("/composition/fill-report-block", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Visualizations (plan 2026-09-02 fill-with-chat-report-blocks, R4)
// ---------------------------------------------------------------------------

export type Visualization = components["schemas"]["VisualizationDTO"];
export type VisualizationList = components["schemas"]["VisualizationListDTO"];
/** Which product surface a visualization is being picked for. */
export type VisualizationSurface = Visualization["surfaces"][number];

/**
 * The whole registry, every row, in registry order.
 *
 * Deliberately NOT filtered server-side by surface or status: the gallery
 * shows the unavailable rows too (greyed, with their status), so the list
 * doubles as the roadmap. Filtering here would turn "coming soon" into
 * "does not exist" — see `api/constants/visualizations.py`.
 */
export async function listVisualizations(): Promise<VisualizationList> {
  return apiJson<VisualizationList>("/composition/visualizations");
}

// ---------------------------------------------------------------------------
// Dashboard composer (plan 2026-09-03 dashboard from an instruction)
// ---------------------------------------------------------------------------

export type ComposeRequest = components["schemas"]["ComposeRequest"];
export type ComposeResponse = components["schemas"]["ComposeResponse"];
/** One data block the composer added (or asked to refill): the tile it wrote,
 *  the block it is waiting to become, and the brief the fill answers. */
export type ComposePlaceholder = components["schemas"]["ComposePlaceholderDTO"];
/** One existing block a turn changed through its own block edit call
 *  (ADR-0475); `item_id` is empty for a new smart text. */
export type ComposeEdited = components["schemas"]["ComposeEditedDTO"];
/** One block a turn could not change; it stays exactly as it was. */
export type ComposeEditFailure = components["schemas"]["ComposeEditFailureDTO"];

/**
 * The item ids a turn changed through a block edit call, ready to refresh.
 *
 * Missing `edited` reads as empty: a backend from before ADR-0475 omits it,
 * and that turn changed nothing through an edit call. A new smart text has an
 * empty id (it arrives with the structure reload), and a block listed under
 * `edit_failures` stayed as it was, so neither is refreshed.
 */
export function editedItemIds(result: Pick<ComposeResponse, "edited" | "edit_failures">): string[] {
  const failed = new Set((result.edit_failures ?? []).map((f) => f.item_id).filter(Boolean));
  const ids: string[] = [];
  for (const entry of result.edited ?? []) {
    if (!entry.item_id || failed.has(entry.item_id) || ids.includes(entry.item_id)) continue;
    ids.push(entry.item_id);
  }
  return ids;
}

/**
 * What the panel shows as the turn's answer. `summary` is the model's own
 * answer; `assistant_text` adds one sentence per `edit_failures` entry (for
 * the next turn's history and MCP clients), which the panel lists itself. A
 * backend that predates `summary` sends only `assistant_text`.
 */
export function composerSummary(result: Pick<ComposeResponse, "assistant_text"> & { summary?: string }): string {
  return result.summary ?? result.assistant_text;
}

export type UndoComposeRequest = components["schemas"]["UndoComposeRequest"];
export type UndoComposeResponse = components["schemas"]["UndoComposeResponse"];

/** One option the composer offers when it asks a question. Server constants,
 *  never fields the model fills in — the user is picking a permission, and a
 *  model-authored button label would be model-controlled UI text. */
export type ComposeChoice = components["schemas"]["ComposeChoiceDTO"];

/** How far a restructure of one tab may go (plan 2026-09-03 arrange,
 *  Decision 2). It is not a prompt convention but the SHAPE OF THE SCHEMA for
 *  that turn: the operations outside the scope are left out of the model's
 *  vocabulary, so an out-of-scope edit is unrepresentable rather than
 *  validated away afterwards. */
export type ArrangeScope = NonNullable<ComposeRequest["arrange_scope"]>;

/**
 * The three-way scope question.
 *
 * Re-exported from the generated module, not written out here. The frontend
 * needs the text because the **Tidy up** button must be able to ask WITHOUT
 * spending a turn — `compose_turn` calls the model before it parses anything,
 * so a button press that posted with no scope would burn a full LLM call and a
 * round trip to be handed back three strings. But a hand-mirrored copy is a
 * second source of truth that nothing keeps honest, so these travel the way
 * the block sizes do: emitted from `api/constants/arrange_scopes.py` by
 * `make generate-types`, committed, and guarded by
 * `scripts/ci/openapi-spec-drift.sh`. Reword them in Python and CI fails here
 * instead of the two paths quietly reading differently.
 *
 * A restructure instruction the user TYPED still comes back from the server
 * with `applied: false` and these same three in `ComposeResponse.choices` —
 * only the model can tell that "make this a proper dashboard" was one — and
 * the panel renders whichever set it has.
 */
export const ARRANGE_CHOICES: readonly ComposeChoice[] = GENERATED_ARRANGE_CHOICES;

/**
 * One turn of the dashboard composer (plan 2026-09-03).
 *
 * The block builder's big brother: instead of one query for one tile, it
 * plans the whole tab in the edit drawer's own vocabulary (rows from the
 * row templates, blocks of the five types) and applies that plan in one
 * transaction. Every data block it adds lands as a PLACEHOLDER carrying its
 * title and brief; `placeholders`/`refills` are what the caller then fans
 * out over `fillBlock({ save: true })`.
 *
 * `applied: false` is not an error: the composer is asking a question or
 * refusing, and `assistant_text` is what to show. When it is asking, the
 * options are in `choices` and the answer comes back as `arrange_scope` on the
 * NEXT request — one request, not a stored preference: a scope kept across
 * turns would silently widen every later instruction in the conversation to
 * whatever the user once picked, `full` included, which removes blocks.
 */
export async function composeDashboard(body: ComposeRequest): Promise<ComposeResponse> {
  const result = await apiJson<ComposeResponse>("/composition/compose", {
    method: "POST",
    body: JSON.stringify(body),
  });
  // The composer's writes live under /composition, not /dashboards/<id>/…,
  // so the dashboards wrapper cannot see them: notify by the body's id
  // (ADR-0228). A refused or questioning turn wrote nothing, but the answer
  // is cheap and the alternative is a second "did it apply" rule here.
  notifyDashboardMutated(body.dashboard_id ?? ANY_DASHBOARD);
  return result;
}

/**
 * Undo the composer's latest turn on one conversation (Decision 6).
 *
 * Single level, latest turn only. A 409 with a readable `detail` is the
 * normal answer when the tab moved on since (a block renamed, deleted or
 * dragged elsewhere by anyone), so show the sentence rather than retrying.
 */
export async function undoCompose(body: UndoComposeRequest): Promise<UndoComposeResponse> {
  const result = await apiJson<UndoComposeResponse>("/composition/undo", {
    method: "POST",
    body: JSON.stringify(body),
  });
  notifyDashboardMutated((body as { dashboard_id?: string }).dashboard_id ?? ANY_DASHBOARD);
  return result;
}

/** One turn of a block's chat, as the editor's Chat pane shows it. */
export type BlockConversationMessage = components["schemas"]["BlockConversationMessageDTO"];
/** A block's own chat, or an empty one when nobody has chatted about it. */
export type BlockConversation = components["schemas"]["BlockConversationDTO"];

/**
 * This caller's chat about ONE block, so the Chat pane can show what was said
 * before (plan 2026-09-03 edit chrome, step 5).
 *
 * The workspace is not optional in practice: the tile gate refuses a block on
 * a workspace-scoped dashboard without it, and it fails closed — pass the same
 * value `fillBlock` gets. Someone else's chat about the same block is never
 * returned; the server filters on the account AND the user, exactly as it does
 * when it decides which conversation the next turn continues.
 */
export async function getBlockConversation(
  itemId: string,
  workspaceId?: string | null,
): Promise<BlockConversation> {
  const query = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : "";
  return apiJson<BlockConversation>(
    `/composition/blocks/${encodeURIComponent(itemId)}/conversation${query}`,
  );
}
